import { SidebarItem, SidebarType } from '@/utils/subscription/types'

import { KEYWORDS_BY_CREATE_ACTION, KEYWORDS_BY_SIDEBAR_ITEM } from './omnibox-keywords'

/** A place the omnibox can take the user to. */
export type OmniboxAction = {
  id: string
  label: string
  link: string
  icon: SidebarItem['icon']
  /** Extra words users type for this action (FR + EN), matched like the label. */
  keywords: string[]
}

/** An action that only exists when the user can see the sidebar entry it depends on. */
export type OmniboxCreateAction = Omit<OmniboxAction, 'keywords'> & { requires: SidebarType }

/** Lowercases and strips accents so "Créer" matches "creer". */
export function normalize(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim()
}

/** Splits on anything that is not a letter or digit. */
export function tokenize(text: string): string[] {
  return normalize(text).split(/[^\p{L}\p{N}]+/u).filter(Boolean)
}

/**
 * Builds the omnibox actions from the entries the user can already see in the sidebar
 * (so licence filtering stays in one place), plus the "create" shortcuts whose parent entry is visible.
 * `translate` turns the sidebar i18n keys into labels.
 */
export function buildOmniboxActions(
  sidebarItems: SidebarItem[],
  createActions: OmniboxCreateAction[],
  translate: (key: string) => string
): OmniboxAction[] {
  const visibleNames = new Set(sidebarItems.map((item) => item.name))
  const creates = createActions
    .filter((action) => visibleNames.has(action.requires))
    .map(({ requires: _requires, ...action }) => ({ ...action, keywords: KEYWORDS_BY_CREATE_ACTION[action.id] ?? [] }))
  const navigations = sidebarItems.map((item) => ({
    id: item.name,
    label: translate(item.label),
    link: item.link,
    icon: item.icon,
    keywords: KEYWORDS_BY_SIDEBAR_ITEM[item.name] ?? [],
  }))
  return [...creates, ...navigations]
}

/**
 * Everyday destinations, most common first: shown as chips before the user types anything,
 * and preferred when several actions match equally ("salle" → waiting rooms before "Salle d'urgence").
 */
export const SUGGESTED_ACTION_IDS: string[] = [
  SidebarType.WaitingRooms,
  'create-patient',
  SidebarType.Patients,
  SidebarType.Consultations,
  SidebarType.Calendar,
  SidebarType.Tutorials,
]

/** Scores one query token against an action: label prefix > keyword prefix > substring; 0 = no match. */
function scoreToken(token: string, labelWords: string[], keywordWords: string[]): number {
  if (labelWords.some((word) => word.startsWith(token))) return 3
  if (keywordWords.some((word) => word.startsWith(token))) return 2
  if (token.length >= 3 && [...labelWords, ...keywordWords].some((word) => word.includes(token))) return 1
  return 0
}

/** Position in the everyday list; anything else ranks after it. */
function everydayRank(id: string): number {
  const rank = SUGGESTED_ACTION_IDS.indexOf(id)
  return rank === -1 ? SUGGESTED_ACTION_IDS.length : rank
}

/**
 * Returns the actions matching every token of the query, best first.
 * Ties go to everyday destinations, then keep the input order (create shortcuts, then sidebar order).
 */
export function matchOmniboxActions(query: string, actions: OmniboxAction[], limit = 5): OmniboxAction[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) return []
  const scored = actions.map((action, index) => {
    const labelWords = tokenize(action.label)
    const keywordWords = action.keywords.flatMap(tokenize)
    const scores = tokens.map((token) => scoreToken(token, labelWords, keywordWords))
    const score = scores.includes(0) ? 0 : scores.reduce((sum, value) => sum + value, 0)
    return { action, index, score, rank: everydayRank(action.id) }
  })
  return scored
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.rank - b.rank || a.index - b.index)
    .slice(0, limit)
    .map((entry) => entry.action)
}

const QUESTION_STARTERS = ['comment', 'pourquoi', 'quel', 'quelle', 'quels', 'quelles', 'ou', 'est', 'peux', 'puis', 'how', 'what', 'why', 'where', 'can', 'which']

/** True when the text reads like a question or instruction for ELARA rather than a search term. */
export function looksLikeQuestion(query: string): boolean {
  const trimmed = query.trim()
  if (trimmed.endsWith('?')) return true
  const tokens = tokenize(trimmed)
  return tokens.length >= 5 || (tokens.length >= 2 && QUESTION_STARTERS.includes(tokens[0]))
}

/** Picks the suggestion chips shown under the empty input. */
export function pickSuggestedActions(actions: OmniboxAction[], max = 4): OmniboxAction[] {
  const byId = new Map(actions.map((action) => [action.id, action]))
  return SUGGESTED_ACTION_IDS.flatMap((id) => byId.get(id) ?? []).slice(0, max)
}
